// Reading and writing chapter commentary. A missing chapter is `null`, not an
// error: that is the ordinary case, and for the admin it is the invitation to
// start writing.
import type { CommentaryInput, CommentarySummary, CommentaryView } from "@bibleunleashed/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, del, put } from "./api";

const key = (osis: string) => ["commentary", osis];
const INDEX_KEY = ["commentary", "index"];

export function useCommentary(osis: string | undefined) {
  return useQuery({
    queryKey: key(osis ?? ""),
    enabled: Boolean(osis),
    queryFn: async () => {
      try {
        return await api<CommentaryView>(`/api/commentary/${encodeURIComponent(osis!)}`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
  });
}

export function useCommentaryIndex() {
  return useQuery({ queryKey: INDEX_KEY, queryFn: () => api<CommentarySummary[]>("/api/commentary") });
}

export function useSaveCommentary(osis: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (doc: CommentaryInput) => put<CommentaryView>(`/api/commentary/${encodeURIComponent(osis)}`, doc),
    onSuccess: (saved) => {
      qc.setQueryData(key(osis), saved);
      void qc.invalidateQueries({ queryKey: INDEX_KEY });
    },
  });
}

export function useDeleteCommentary(osis: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => del<null>(`/api/commentary/${encodeURIComponent(osis)}`),
    onSuccess: () => {
      qc.setQueryData(key(osis), null);
      void qc.invalidateQueries({ queryKey: INDEX_KEY });
    },
  });
}
